import { Suspense } from "react";
import { Link } from "react-router-dom";
import truncate from "lodash/truncate";
import { Spinner } from "reactstrap";
import { useNewsArticle } from "../../hooks/NewsHooks";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { getSlug } from "../../utils/CmsUtils";
import Herobox from "../Herobox";
import GenericSlideThumb from "./SlideThumb";
import Icon from "../Icon";
import SimpleLoadingSpinner from "../SimpleLoadingSpinner";
import classNames from "classnames";
import GenericSlideTab from "./SlideTab";

const NewsSlide = props => {
  const { news_id, className, deferLoading, ...rest } = props;
  return (
    <Suspense
      fallback={
        <Herobox
          className={classNames(className, "loading")}
          theme="black"
          type="fat"
          centerImage
          textShadow
        >
          <SimpleLoadingSpinner />
        </Herobox>
      }
    >
      <NewsSlideInner
        {...rest}
        news_id={news_id}
        className={className}
        deferLoading={deferLoading}
      />
    </Suspense>
  );
};

const NewsSlideInner = ({
  news_id,
  className,
  deferLoading = false,
  ...rest
}) => {
  const article = useNewsArticle(news_id, true);
  const t = useBjelinTranslations();

  if (!article) return null;
  const isLongTitle = article.heading.length > 80;

  return (
    <Herobox
      {...rest}
      className={className}
      theme="black"
      type="fat"
      backgroundImage={article.image?.styles?.full || article.image.path}
      centerImage
      textShadow
      deferLoading={deferLoading}
      ariaLabel={article.heading}
    >
      <div className="cockpit-bjelin_heading text-center">
        <h2>
          <span style={{ fontSize: isLongTitle ? "0.8em" : "1em" }}>
            {article.heading}
          </span>
        </h2>
      </div>
      <div className="cockpit-bjelin_button text-center">
        <Link
          className={"button-outlined"}
          to={"/news/" + article.id + "-" + getSlug(article.heading)}
        >
          {t("read.more")} <Icon name="chevron-right" />
        </Link>
      </div>
    </Herobox>
  );
};

export const NewsSlideThumb = ({ settings, isActive, onClick }) => {
  return (
    <Suspense
      fallback={
        <div className="slide-thumb">
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#333",
              color: "#fff"
            }}
          >
            <Spinner />
          </div>
        </div>
      }
    >
      <NewsSlideThumbInner
        settings={settings}
        isActive={isActive}
        onClick={onClick}
      />
    </Suspense>
  );
};

const NewsSlideThumbInner = props => {
  const article = useNewsArticle(props.settings.news_id, true);
  if (!article) return null;

  const label = truncate(article?.heading, {
    length: 64,
    separator: /,? +/
  });

  return (
    <GenericSlideThumb
      image={article.image?.styles?.preview || article.image.path}
      label={label}
      isActive={props.isActive}
      onClick={props.onClick}
    />
  );
};

export const NewsSlideTab = ({ settings, isActive, onClick }) => {
  return (
    <Suspense
      fallback={
        <div className="slide-tab">
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#333",
              color: "#fff"
            }}
          >
            <Spinner />
          </div>
        </div>
      }
    >
      <NewsSlideTabInner
        settings={settings}
        isActive={isActive}
        onClick={onClick}
      />
    </Suspense>
  );
};

const NewsSlideTabInner = props => {
  const article = useNewsArticle(props.settings.news_id, true);
  if (!article) return null;

  const label = truncate(article?.heading, {
    length: 64,
    separator: /,? +/
  });

  return (
    <GenericSlideTab
      image={article.image?.styles?.preview || article.image.path}
      label={label}
      isActive={props.isActive}
      onClick={props.onClick}
    />
  );
};

export default NewsSlide;
